/**
 * Device / identity fingerprint gathered at every ToS acceptance and stored
 * server-side alongside the IP chain for identity-verification purposes.
 * Everything here is best-effort: any probe that throws is simply omitted.
 */

function safe(fn) {
  try { const v = fn(); return v === undefined ? null : v; } catch { return null; }
}

async function hashHex(str) {
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    // Insecure-context fallback: djb2
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return `djb2:${(h >>> 0).toString(16)}`;
  }
}

async function canvasHash() {
  const c = document.createElement("canvas");
  c.width = 220; c.height = 40;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  ctx.textBaseline = "top";
  ctx.font = "14px 'Arial'";
  ctx.fillStyle = "#f60"; ctx.fillRect(60, 4, 100, 24);
  ctx.fillStyle = "#069"; ctx.fillText("Playden ToS fp \u{1F0CF} 1.0", 2, 8);
  ctx.fillStyle = "rgba(102, 204, 0, 0.7)"; ctx.fillText("Playden ToS fp \u{1F0CF} 1.0", 4, 10);
  return hashHex(c.toDataURL());
}

function webglInfo() {
  const c = document.createElement("canvas");
  const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
  if (!gl) return null;
  const dbg = gl.getExtension("WEBGL_debug_renderer_info");
  return {
    vendor: dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
    renderer: dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
  };
}

export async function collectClientInfo() {
  const n = navigator;
  const info = {
    collected_at: new Date().toISOString(),
    local_time: safe(() => new Date().toString()),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    timezone_offset_min: safe(() => new Date().getTimezoneOffset()),
    locale: safe(() => Intl.DateTimeFormat().resolvedOptions().locale),
    user_agent: safe(() => n.userAgent),
    platform: safe(() => n.platform),
    vendor: safe(() => n.vendor),
    language: safe(() => n.language),
    languages: safe(() => [...(n.languages || [])]),
    hardware_concurrency: safe(() => n.hardwareConcurrency),
    device_memory_gb: safe(() => n.deviceMemory),
    max_touch_points: safe(() => n.maxTouchPoints),
    cookies_enabled: safe(() => n.cookieEnabled),
    do_not_track: safe(() => n.doNotTrack),
    webdriver: safe(() => n.webdriver),
    pdf_viewer: safe(() => n.pdfViewerEnabled),
    ua_data: safe(() => n.userAgentData ? {
      brands: n.userAgentData.brands, mobile: n.userAgentData.mobile, platform: n.userAgentData.platform,
    } : null),
    screen: safe(() => ({
      width: window.screen.width, height: window.screen.height,
      avail_width: window.screen.availWidth, avail_height: window.screen.availHeight,
      color_depth: window.screen.colorDepth, pixel_depth: window.screen.pixelDepth,
      orientation: window.screen.orientation?.type || null,
    })),
    viewport: safe(() => ({ width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio })),
    connection: safe(() => {
      const c = n.connection; if (!c) return null;
      return { effective_type: c.effectiveType, downlink: c.downlink, rtt: c.rtt, save_data: c.saveData };
    }),
    referrer: safe(() => document.referrer || null),
    page_url: safe(() => window.location.href),
    history_length: safe(() => window.history.length),
    webgl: safe(() => webglInfo()),
    touch_support: safe(() => "ontouchstart" in window),
    storage_quota_mb: null,
    canvas_hash: null,
  };
  try { info.canvas_hash = await canvasHash(); } catch { /* omitted */ }
  try {
    const est = await navigator.storage?.estimate?.();
    if (est?.quota) info.storage_quota_mb = Math.round(est.quota / 1048576);
  } catch { /* omitted */ }
  return info;
}
